import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './SolutionsModal.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faInfoCircle,
  faNewspaper,
  faMeteor,
  faArrowUp19,
  faPersonWalkingLuggage,
  faPersonWalkingArrowLoopLeft,
} from '@fortawesome/free-solid-svg-icons';
import { faRectangleList } from '@fortawesome/free-regular-svg-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

const modalTranslations = {
  es: {
    header: 'Evaluación Ágil y Objetiva de tu Equipo',
    paragraphs: [
      'Convierte la evaluación de tu plantilla en un proceso ágil, cuantificable y libre de sesgos. Conoce al instante dónde tienes fortalezas para potenciar y áreas críticas que requieren acción.',
    ],
    sections: [
      {
        title: 'Evaluación de Habilidades con Feedback de Compañeros',
        bullets: [
          'Recopila valoraciones anónimas y estructuradas de todos los miembros del equipo.',
          'Define competencias clave y niveles de dominio.',
          'Genera informes gráficos que muestran quién sobresale en cada habilidad.',
          'Detecta gaps de conocimiento para dirigir mejor la formación interna.',
        ],
      },
      {
        title: 'Talent Assessment Process (TAP®): Desempeño y Potencial',
        bullets: [
          'Clasifica a tu gente en función de su rendimiento actual y proyección futura.',
          'Identifica quiénes están listos para asumir más responsabilidades.',
          'Detecta talento con alto potencial que podría estancarse si no recibe reto.',
          'Destaca a las “estrellas en ascenso” y quiénes necesitan acompañamiento.',
        ],
      },
      {
        title: 'Evaluación de Riesgos: Probabilidad e Impacto de Rotación',
        bullets: [
          'Calcula la probabilidad de abandono en los próximos 6–12 meses.',
          'Mide el impacto en proyectos clave y roles críticos.',
          'Recibe alertas automáticas para tomar medidas de retención.',
        ],
      },
      {
        title: 'Evaluación de Sucesión',
        bullets: [
          'Mapea rutas de desarrollo personalizadas.',
          'Compara perfiles internos con requisitos de cada puesto.',
          'Confecciona planes de formación y mentoring para transiciones sin fisuras.',
        ],
      },
    ],
    benefitsText:
      'Mira todos los beneficios que puedes obtener al poner en práctica una estrategia como esta:',
    sourceLabel: ' Fuente',
    benchText:
      'Según DDI, solo el 20% de las organizaciones confía en su bench de líderes preparados. Las empresas Bether triplican esa capacidad interna, cubriendo un 60% de los puestos críticos.',
    turnoverText:
      'Randstad sitúa el churn anual en una media del 17% en España. Un programa de sucesión y retención efectivo (como el de Bether) lo reduce en un 31%.',
    costText:
      'Reemplazar un empleado cuesta de media el 33% de su salario anual (≈€9 980). Al bajar la rotación un 31%, Bether evita alrededor de €3 090 por empleado anualmente.',
  },
  en: {
    header: 'Agile, Objective Team Assessment',
    paragraphs: [
      'Turn your workforce evaluation into an agile, measurable, and bias-free process. Instantly uncover strengths to leverage and critical areas requiring action.',
    ],
    sections: [
      {
        title: 'Skills Assessment with Peer Feedback',
        bullets: [
          'Collect anonymous, structured feedback from all team members.',
          'Define key competencies and mastery levels.',
          'Generate graphical reports showing top performers for each skill.',
          'Identify knowledge gaps for targeted internal training.',
        ],
      },
      {
        title: 'Talent Assessment Process (TAP®): Performance & Potential',
        bullets: [
          'Classify your people by current performance and future potential.',
          'Identify who is ready for more responsibility.',
          'Spot high-potential talent at risk of stagnation without challenge.',
          'Highlight rising stars and those needing close support.',
        ],
      },
      {
        title: 'Risk Assessment: Turnover Probability & Impact',
        bullets: [
          'Estimate turnover probability over the next 6–12 months.',
          'Assess impact on key projects and critical roles.',
          'Receive automatic alerts to implement retention measures.',
        ],
      },
      {
        title: 'Succession Assessment',
        bullets: [
          'Map personalized development pathways.',
          'Compare internal profiles against job requirements.',
          'Create training and mentoring plans for seamless transitions.',
        ],
      },
    ],
    benefitsText:
      'See all the benefits you can gain by implementing a strategy like this:',
    sourceLabel: ' Source',
    benchText:
      'According to DDI, only 20% of organizations trust their bench of prepared leaders. Bether triples that capacity, covering 60% of critical positions.',
    turnoverText:
      'Randstad places the annual churn rate at 17% in Spain. An effective succession and retention program (like Bether) reduces it by 31%.',
    costText:
      'Replacing an employee costs on average 33% of annual salary (≈€9,980). By reducing turnover by 31%, Bether saves €3,090 per employee annually.',
  },
};

const sectionIcons = [
  faRectangleList,
  faArrowUp19,
  faPersonWalkingLuggage,
  faPersonWalkingArrowLoopLeft,
];
const teiMetrics = [
  {
    metric: 'Bench Strength',
    baseline: 20,
    improved: 60,
    unit: '%',
    center: '×3',
    link: 'https://www.ddiworld.com/global-leadership-forecast-2021',
  },
  {
    metric: 'Turnover Risk',
    baseline: 17,
    improved: 11.7,
    unit: '%',
    center: '-31%',
    link: 'https://www.randstadresearch.es/tendencias-de-rrhh/',
  },
  {
    metric: 'Cost Avoided',
    baseline: 9980,
    improved: 3090,
    unit: '€',
    center: '-66%',
    link: 'https://www.equiposytalento.com/noticias/2023/03/06/el-coste-medio-de-reemplazar-a-un-empleado-en-espana-asciende-a-7400-euros',
  },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload.find((p) => p.name);
    if (!entry) return null;
    const { name, value, payload: data } = entry;
    const unit = data.unit || '';
    return (
      <div className="tooltip-box">
        <strong>{name}</strong>: {value.toFixed(1)}
        {unit}
      </div>
    );
  }
  return null;
};

const TEICharts = ({ language }) => {
  const [infoVisible, setInfoVisible] = useState({});
  const toggleInfo = (metric) =>
    setInfoVisible((prev) => ({ ...prev, [metric]: !prev[metric] }));
  const sourceLabel = modalTranslations[language].sourceLabel;
  const text = modalTranslations[language];

  return (
    <div className="smodal-tei-charts">
      {teiMetrics.map(({ metric, baseline, improved, unit, center, link }) => {
        const basePct = unit === '%' ? baseline : 100;
        const improvedPct =
          unit === '%' ? improved : (improved / baseline) * 100;
        const outerData = [
          { name: 'Bether Average', value: improvedPct, unit },
          { name: '', value: 100 - improvedPct },
        ];
        const innerData = [
          { name: 'Business Average', value: basePct, unit },
          { name: '', value: 100 - basePct },
        ];
        const tooltipText =
          metric === 'Bench Strength'
            ? text.benchText
            : metric === 'Turnover Risk'
              ? text.turnoverText
              : text.costText;

        return (
          <div key={metric} className="smodal-tei-chart-card">
            <h4 className="smodal-tei-chart-title">{metric}</h4>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={outerData}
                  dataKey="value"
                  nameKey="name"
                  startAngle={90}
                  endAngle={-270}
                  innerRadius="65%"
                  outerRadius="95%"
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {outerData.map((_, idx) => (
                    <Cell key={idx} fill={idx === 0 ? '#ff2300' : '#e0e0e0'} />
                  ))}
                </Pie>
                <Pie
                  data={innerData}
                  dataKey="value"
                  nameKey="name"
                  startAngle={90}
                  endAngle={-270}
                  innerRadius="45%"
                  outerRadius="63%"
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {innerData.map((_, idx) => (
                    <Cell key={idx} fill={idx === 0 ? '#555555' : '#e0e0e0'} />
                  ))}
                </Pie>
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="smodal-tei-chart-center"
                >
                  {center}
                </text>
                <RechartsTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="info-icon"
              onClick={() => toggleInfo(metric)}
            />
            {infoVisible[metric] && (
              <div className="tooltip-popup">
                <div className="tooltip-text">{tooltipText}</div>
                <button
                  className="source-button"
                  onClick={() => window.open(link, '_blank')}
                >
                  <FontAwesomeIcon icon={faNewspaper} />
                  &nbsp;{sourceLabel}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SolutionsModal1 = ({ isOpen, onClose, language = 'es' }) => {
  if (!isOpen) return null;
  const t = modalTranslations[language];

  return (
    <div className="smodal-overlay">
      <div className="smodal-content">
        <button className="smodal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="smodal-header">{t.header}</h2>
        {t.paragraphs.map((text, idx) => (
          <p key={idx}>{text}</p>
        ))}
        {t.sections.map((section, sidx) => (
          <div key={sidx} className="smodal-section">
            <FontAwesomeIcon
              icon={sectionIcons[sidx]}
              className="section-icon"
            />
            <div className="section-content">
              <h3 className="section-title">{section.title}</h3>
              <ul className="section-list">
                {section.bullets.map((b, bidx) => (
                  <li key={bidx}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
        <div className="smodal-tei-box">
          <h3 className="smodal-tei-title">
            <FontAwesomeIcon icon={faMeteor} className="tei-icon" /> TEI: Total
            Economic Impact
          </h3>
          <p className="smodal-tei-intro">{t.benefitsText}</p>
          <TEICharts language={language} />
        </div>
      </div>
    </div>
  );
};

SolutionsModal1.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  language: PropTypes.oneOf(['es', 'en']),
};

export default SolutionsModal1;
